import { EventKind, type Specimen } from '@numistic/contracts'
import {
  useCatalogItem,
  useCreateEvent,
  useCreateNote,
  useCreateTag,
  useDuplicateSpecimen,
  useMeta,
  useRemoveEvent,
  useRemoveNote,
  useRemoveSpecimen,
  useSpecimen,
  useTree,
  useTags,
  useUpdateSpecimen,
} from '@numistic/data'
import { useTranslation } from '@numistic/i18n'
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Input,
  Select,
  Textarea,
} from '@numistic/ui'
import { createRoute, Link, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'

import { ImageGallery } from '../../components/image-gallery.js'
import { PageLoading } from '../../components/page-loading.js'
import { MutationErrors } from '../../components/mutation-errors.js'
import { cleanEvent, EventFields, type EventDraft } from '../../collections/event-fields.js'
import { BaseHint, ValuationsBox } from '../../collections/valuations.js'
import { useErrorText } from '../../lib/errors.js'
import { formatDate, formatDateTime, formatMoney, localToday } from '../../lib/format.js'
import { requireSession } from '../../lib/guards.js'
import { MetaView } from '../../meta/meta-view.js'
import { rootRoute } from '../root.js'

export const specimenRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/s/$id',
  beforeLoad: ({ context, location }) => requireSession(context, location),
  component: SpecimenPage,
})

function SpecimenPage() {
  const { t } = useTranslation()
  const errorText = useErrorText()
  const { id } = specimenRoute.useParams()
  const specimen = useSpecimen(id)
  // Названия разновидностей записи каталога — для поля «Разновидность».
  const catalogItem = useCatalogItem(specimen.data?.variantId ? specimen.data.catalogItem.id : '')
  const meta = useMeta().data
  const remove = useRemoveSpecimen()
  const duplicate = useDuplicateSpecimen()
  const navigate = useNavigate()
  if (specimen.error)
    return <Alert variant="destructive">{errorText.request(specimen.error)}</Alert>
  const data = specimen.data
  if (!data || !meta) return <PageLoading />
  const status = meta.statuses.find((s) => s.id === data.values.status)
  const images = data.images.length ? data.images : data.catalogImages
  const grade = data.resolved.values.grade as { scale: string; value: string } | undefined
  const gradeLabel = grade
    ? (meta.gradeScales
        .find((g) => g.code === grade.scale)
        ?.values.find((v) => v.code === grade.value)?.code ?? grade.value)
    : null
  const title = String(data.resolved.values.title ?? '')

  return (
    <article className="grid gap-6">
      <div className="flex flex-wrap items-start gap-3">
        <div className="mr-auto grid gap-1">
          <CollectionPath id={String(data.values.collection ?? '')} />
          <h1 className="flex flex-wrap items-center gap-x-3 gap-y-1 text-2xl font-semibold">
            {title}
            {status ? (
              <span
                className="rounded px-2 py-0.5 text-sm font-normal"
                style={{ backgroundColor: `${status.color}22`, color: status.color }}
              >
                {status.name.ru}
              </span>
            ) : null}
            {gradeLabel ? (
              <span className="rounded border px-2 py-0.5 text-sm font-normal">{gradeLabel}</span>
            ) : null}
          </h1>
          <Link
            to="/catalog/$id"
            params={{ id: data.catalogItem.id }}
            className="text-sm text-primary underline underline-offset-4 hover:decoration-2"
          >
            {t('specimen.fromCatalog')}: {data.catalogItem.title}
          </Link>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button asChild variant="outline">
            <Link to="/s/$id/edit" params={{ id }} search={{}}>
              {t('catalog.edit')}
            </Link>
          </Button>
          <Button
            variant="outline"
            disabled={duplicate.isPending}
            onClick={() =>
              duplicate.mutate(id, {
                onSuccess: (s) => void navigate({ to: '/s/$id', params: { id: s.id } }),
              })
            }
          >
            {t('specimen.duplicate')}
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              if (window.confirm(t('specimen.deleteConfirm')))
                remove.mutate(id, { onSuccess: () => void navigate({ to: '/c', search: {} }) })
            }}
          >
            {t('catalog.delete')}
          </Button>
        </div>
      </div>
      <MutationErrors errors={[remove.error, duplicate.error]} />
      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]">
        {/* Фото и теги остаются на месте, пока листаются поля и журнал. */}
        <div className="grid content-start gap-3 lg:sticky lg:top-4">
          <ImageGallery
            images={images}
            title={title}
            note={!data.images.length && images.length ? t('specimen.catalogPhotos') : undefined}
          />
          {/* На широком экране теги под фото; на телефоне — после полей (см. ниже). */}
          <div className="hidden lg:block">
            <TagsBox specimen={data} />
          </div>
        </div>
        <div className="grid min-w-0 gap-6">
          <Summary specimen={data} />
          <Card>
            <CardContent className="pt-6">
              <MetaView
                typeCode={data.catalogItem.typeCode}
                level="specimen"
                values={data.resolved.values}
                sources={data.resolved.sources}
                hide={['title', 'collection', 'status']}
                refs={{ own: true, variants: catalogItem.data?.variants }}
                grouped
                sectionOrder={['specimen', 'main', 'physical', 'paper', 'design', 'notes']}
              />
            </CardContent>
          </Card>
          <div className="lg:hidden">
            <TagsBox specimen={data} />
          </div>
          <EventsBox specimen={data} />
          <ValuationsBox specimen={data} />
          <NotesBox specimen={data} />
        </div>
      </div>
    </article>
  )
}

/**
 * Главное об экземпляре одной строкой: покупка, последняя оценка (и изменение к цене покупки
 * в базовой валюте), место хранения.
 */
function Summary({ specimen }: { specimen: Specimen }) {
  const { t } = useTranslation()
  const locations = useTree('locations').data ?? []
  const bought = specimen.events.find((e) => e.kind === 'acquired')
  const value = specimen.valuations.at(-1)
  const locationId = specimen.values.storage_location
  const byId = new Map(locations.map((n) => [n.id, n]))
  const where: string[] = []
  for (
    let n = typeof locationId === 'string' ? byId.get(locationId) : undefined;
    n;
    n = n.parentId ? byId.get(n.parentId) : undefined
  )
    where.unshift(n.name)
  const cost = bought?.amountBase ? Number(bought.amountBase) : null
  const worth = value?.amountBase ? Number(value.amountBase) : null
  const change =
    cost && worth !== null && bought?.baseCurrency === value?.baseCurrency
      ? Math.round(((worth - cost) / cost) * 100)
      : null
  const day = (iso: string) => formatDate(new Date(`${iso}T00:00:00`))
  const tile = 'grid content-start gap-0.5 rounded-lg border bg-card p-3'
  return (
    <div className="grid gap-3 sm:grid-cols-3">
      <div className={tile}>
        <span className="text-xs text-muted-foreground">{t('specimen.summary.bought')}</span>
        {bought ? (
          <>
            <span className="text-base font-semibold tabular-nums">
              {bought.amount && bought.currency ? formatMoney(bought.amount, bought.currency) : '—'}
            </span>
            <span className="text-xs text-muted-foreground">
              {[day(bought.occurredOn), bought.counterparty].filter(Boolean).join(' · ')}
            </span>
          </>
        ) : (
          <span className="text-sm text-muted-foreground">{t('specimen.summary.none')}</span>
        )}
      </div>
      <div className={tile}>
        <span className="text-xs text-muted-foreground">{t('specimen.summary.value')}</span>
        {value ? (
          <>
            <span className="text-base font-semibold tabular-nums">
              {formatMoney(value.amount, value.currency)}
              {change !== null && change !== 0 ? (
                <span
                  className={
                    change > 0
                      ? 'ml-2 text-xs text-emerald-600 dark:text-emerald-400'
                      : 'ml-2 text-xs text-destructive'
                  }
                >
                  {change > 0 ? '+' : ''}
                  {change}%
                </span>
              ) : null}
            </span>
            <span className="text-xs text-muted-foreground">{day(value.valuedOn)}</span>
          </>
        ) : (
          <span className="text-sm text-muted-foreground">{t('specimen.summary.none')}</span>
        )}
      </div>
      <div className={tile}>
        <span className="text-xs text-muted-foreground">{t('specimen.summary.where')}</span>
        {where.length ? (
          <Link
            to="/settings/locations"
            search={{}}
            className="text-sm font-medium hover:text-primary hover:underline"
          >
            {where.join(' / ')}
          </Link>
        ) : (
          <span className="text-sm text-muted-foreground">{t('specimen.summary.nowhere')}</span>
        )}
      </div>
    </div>
  )
}

/** «Мои коллекции / Монеты / СССР» — где лежит экземпляр, каждое звено кликабельно. */
function CollectionPath({ id }: { id: string }) {
  const { t } = useTranslation()
  const tree = useTree('collections').data ?? []
  const byId = new Map(tree.map((n) => [n.id, n]))
  const path: Array<{ id: string; name: string }> = []
  for (let n = byId.get(id); n; n = n.parentId ? byId.get(n.parentId) : undefined) path.unshift(n)
  return (
    <nav
      aria-label={t('specimen.location')}
      className="flex flex-wrap items-center gap-1 text-sm text-muted-foreground"
    >
      <Link to="/c" search={{}} className="hover:text-foreground">
        {t('collections.title')}
      </Link>
      {path.map((n) => (
        <span key={n.id} className="flex items-center gap-1">
          <span aria-hidden>/</span>
          <Link to="/c" search={{ collection: n.id }} className="hover:text-foreground">
            {n.name}
          </Link>
        </span>
      ))}
    </nav>
  )
}

function TagsBox({ specimen }: { specimen: Specimen }) {
  const { t } = useTranslation()
  const tags = useTags().data ?? []
  const update = useUpdateSpecimen()
  const createTag = useCreateTag()
  const [name, setName] = useState('')
  const toggle = (tagId: string, on: boolean) =>
    update.mutate({
      id: specimen.id,
      version: specimen.version,
      values: specimen.values,
      tagIds: on ? [...specimen.tagIds, tagId] : specimen.tagIds.filter((x) => x !== tagId),
    })
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{t('specimen.tags')}</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-2">
        <div className="flex flex-wrap gap-3">
          {tags.map((tag) => (
            <label key={tag.id} className="flex items-center gap-1.5 text-sm">
              <Checkbox
                checked={specimen.tagIds.includes(tag.id)}
                disabled={update.isPending}
                onChange={(e) => toggle(tag.id, e.target.checked)}
              />
              {tag.name}
            </label>
          ))}
        </div>
        <form
          className="flex gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            if (name.trim())
              createTag.mutate(
                { name: name.trim() },
                { onSuccess: (tag) => (setName(''), toggle(tag.id, true)) },
              )
          }}
        >
          <Input
            value={name}
            placeholder={t('specimen.newTag')}
            aria-label={t('specimen.newTag')}
            onChange={(e) => setName(e.target.value)}
          />
          <Button type="submit" variant="outline" size="sm">
            {t('common.add')}
          </Button>
        </form>
        <MutationErrors errors={[update.error, createTag.error]} />
      </CardContent>
    </Card>
  )
}

const emptyDraft = (): EventDraft => ({ occurredOn: localToday(), currency: 'RUB' })

function EventsBox({ specimen }: { specimen: Specimen }) {
  const { t } = useTranslation()
  const errorText = useErrorText()
  const meta = useMeta().data!
  const create = useCreateEvent(specimen.id)
  const remove = useRemoveEvent()
  const [kind, setKind] = useState<EventKind>('sold')
  const [draft, setDraft] = useState<EventDraft>(emptyDraft)
  const [open, setOpen] = useState(false)
  return (
    <Card>
      <CardHeader className="flex flex-row items-center gap-2">
        <CardTitle className="mr-auto">{t('specimen.events')}</CardTitle>
        <Button variant="outline" size="sm" onClick={() => setOpen((v) => !v)}>
          {t('specimen.eventAdd')}
        </Button>
      </CardHeader>
      <CardContent className="grid gap-3">
        {open ? (
          <form
            className="grid gap-3 rounded-lg border p-3"
            onSubmit={(e) => {
              e.preventDefault()
              // Источник («магазин», «аукцион») — только у покупки.
              const event = cleanEvent(kind === 'acquired' ? draft : { ...draft, sourceCode: null })
              if (event)
                create.mutate(
                  { kind, ...event },
                  { onSuccess: () => (setOpen(false), setDraft(emptyDraft())) },
                )
            }}
          >
            <Select
              aria-label={t('specimen.events')}
              value={kind}
              onChange={(e) => setKind(e.target.value as EventKind)}
            >
              {EventKind.options.map((k) => (
                <option key={k} value={k}>
                  {t(`specimen.eventKinds.${k}`)}
                </option>
              ))}
            </Select>
            <EventFields
              meta={meta}
              value={draft}
              onChange={setDraft}
              withSource={kind === 'acquired'}
            />
            {create.error ? (
              <Alert variant="destructive">{errorText.request(create.error)}</Alert>
            ) : null}
            <Button type="submit" className="justify-self-start">
              {t('common.save')}
            </Button>
          </form>
        ) : null}
        {specimen.events.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('specimen.noEvents')}</p>
        ) : null}
        <ol className="grid gap-2">
          {specimen.events.map((e) => (
            <li
              key={e.id}
              className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-l-2 border-primary/40 pl-3 text-sm"
            >
              <span className="font-medium">{t(`specimen.eventKinds.${e.kind}`)}</span>
              <span className="text-muted-foreground">
                {formatDate(new Date(`${e.occurredOn}T00:00:00`))}
              </span>
              {e.amount && e.currency ? <span>{formatMoney(e.amount, e.currency)}</span> : null}
              {e.amount ? (
                <BaseHint amount={e.amountBase} base={e.baseCurrency} currency={e.currency} />
              ) : null}
              {e.counterparty ? (
                <span className="text-muted-foreground">{e.counterparty}</span>
              ) : null}
              {e.lotNumber ? (
                <span className="text-muted-foreground">лот {e.lotNumber}</span>
              ) : null}
              <button
                type="button"
                className="ml-auto text-xs text-muted-foreground hover:text-foreground"
                onClick={() => remove.mutate(e.id)}
              >
                {t('common.remove')}
              </button>
            </li>
          ))}
        </ol>
        <MutationErrors errors={[remove.error]} />
      </CardContent>
    </Card>
  )
}

function NotesBox({ specimen }: { specimen: Specimen }) {
  const { t } = useTranslation()
  const create = useCreateNote(specimen.id)
  const remove = useRemoveNote()
  const [body, setBody] = useState('')
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('specimen.notes')}</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-3">
        {specimen.notes.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('specimen.noNotes')}</p>
        ) : null}
        <ul className="grid gap-2">
          {specimen.notes.map((n) => (
            <li key={n.id} className="grid gap-1 rounded-md bg-muted/60 p-3 text-sm">
              <span className="whitespace-pre-line">{n.bodyMd}</span>
              <span className="flex gap-3 text-xs text-muted-foreground">
                {formatDateTime(n.createdAt)}
                <button
                  type="button"
                  className="hover:text-foreground"
                  onClick={() => remove.mutate(n.id)}
                >
                  {t('common.remove')}
                </button>
              </span>
            </li>
          ))}
        </ul>
        <MutationErrors errors={[remove.error]} />
        <form
          className="grid gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            if (body.trim()) create.mutate(body.trim(), { onSuccess: () => setBody('') })
          }}
        >
          <Textarea
            aria-label={t('specimen.noteAdd')}
            placeholder={t('specimen.notePlaceholder')}
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
          <Button
            type="submit"
            variant="outline"
            className="justify-self-start"
            disabled={create.isPending}
          >
            {t('specimen.noteAdd')}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}
